// -----------------------------------------------------------------------------
// <copyright file="auth.gaurds.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { firstValueFrom, Observable, of, from, BehaviorSubject } from 'rxjs';
import { filter, switchMap, take, catchError, map } from 'rxjs/operators';
import { GlobalService } from '../../shared/services/global.service';
import { LoaderService } from '../../shared/loader/loader.service';
import { environment } from '../../../environments/environment';
import { LoginService } from '../services/login.service';
import { ModalService } from '../../shared/modals/modal.service';
import { IframeAuthService } from '../services/iframe-auth'; //  adjust path if needed

@Injectable()
export class AuthGuard {
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly globalService = inject(GlobalService);
  private readonly loginService = inject(LoginService);
  private readonly loader = inject(LoaderService);
  private readonly modalService = inject(ModalService);
  private readonly iframeAuth = inject(IframeAuthService); //  injected

  private readonly initializationSubject = new BehaviorSubject<boolean | null>(null);
  private isInitializing = false;

  private readonly isIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  // Only true for genuine MSAL auth popups that carry an auth code/error in the URL.
  // window.open() from an external app (e.g. CPQ) also sets window.opener but has no
  // MSAL auth params — those windows must go through the normal VAS auth flow.
  private readonly isMsalAuthPopup = (() => {
    if (!window.opener || window.opener === window) return false;
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.substring(1));
    return search.has('code') || search.has('error') || hash.has('code') || hash.has('error');
  })();

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    // Genuine MSAL auth popup mid-flow — let MSAL handle the redirect and close the window
    if (this.isMsalAuthPopup) {
      return true;
    }

    //  Iframe — bypass MSAL broadcast wait, use host token flow
    if (this.isIframe) {
      const data = this.handleIframeProtectedRoute(state);
      return true;
    }

    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      switchMap(() => this.handleProtectedRoute(state)),
    );
  }

  //  IFRAME PROTECTED ROUTE — uses host token, no redirect
  private handleIframeProtectedRoute(state: RouterStateSnapshot): Observable<boolean> {
    // Already initialized — allow immediately without calling restoreSession, which
    // calls fetchAndCacheLookupData and closes the loader before component ngOnInits run.
    if (this.globalService.appInitialized()) {
      return of(true);
    }

    // Restore existing app session if present
    const sessionData = this.getStoredSessionData();
    if (sessionData) {
      this.restoreSession(sessionData);
      return of(true);
    }

    // Need host token to build a session
    const hostToken = this.iframeAuth.getToken();
    if (!hostToken) {
      // No token — cannot proceed, but DON'T redirect inside iframe
      this.storeRedirectUrl(state.url);
      this.completeInitialization(false);
      return of(false);
    }

    // Avoid double init
    if (this.isInitializing) {
      return this.initializationSubject.asObservable().pipe(
        filter((val) => val !== null),
        map((val) => val as boolean),
        take(1),
      );
    }

    return this.performIframeLogin(hostToken);
  }

  private performIframeLogin(hostToken: string): Observable<boolean> {
    this.isInitializing = true;
    this.loader.setLoadingState.set(true);

    return from(
      Promise.resolve()
        .then(() => {
          this.storeUserToken(hostToken);
          this.setUserPref({ Token: hostToken });

          this.completeInitialization(true);
          return true;
        })
        .catch(() => {
          this.completeInitialization(false);
          return false;
        }),
    );
  }

  // private performIframeLogin(hostToken: string): Observable<boolean> {
  //   this.isInitializing = true;
  //   this.loader.setLoadingState.set(true);

  //   return from(
  //     firstValueFrom(
  //       this.loginService.getUserDetails().pipe(
  //         switchMap((user) => {
  //           this.storeUserToken(hostToken);
  //           this.setUserPref({
  //             ...user.Result,
  //             Token: hostToken,
  //           });
  //           this.storeUserDetails(user.Result);
  //           return this.loginService.fetchAndCacheLookupData();
  //         }),
  //         switchMap(() => of(true)),
  //         catchError((err) => {
  //           if (err?.Code === '409') {
  //             this.handleConflictError(err?.Message);
  //           }
  //           this.completeInitialization(false);
  //           return of(false);
  //         }),
  //       ),
  //     )
  //       .then((result) => {
  //         this.completeInitialization(result);
  //         return result;
  //       })
  //       .catch(() => {
  //         this.completeInitialization(false);
  //         return false;
  //       }),
  //   );
  // }

  // STANDALONE PROTECTED ROUTE
  private handleProtectedRoute(state: RouterStateSnapshot): Observable<boolean> {
    const request = null;
    if (this.msalService.instance.getAllAccounts().length === 0) {
      this.storeRedirectUrl(state.url);
      this.router.navigate(['']);
      return of(false);
    }

    const sessionData = this.getStoredSessionData();
    if (sessionData) {
      this.restoreSession(sessionData);
      return of(true);
    }

    if (!this.globalService.appInitialized()) {
      if (this.isInitializing) {
        return this.initializationSubject.asObservable().pipe(
          filter((val) => val !== null),
          map((val) => val as boolean),
          take(1),
        );
      }
      return this.performLogin(request);
    }

    return of(true);
  }

  private storeRedirectUrl(url: string): void {
    if (url && url !== '/' && url !== '') {
      localStorage.setItem('vas_redirect_url', url);
    }
  }

  private getStoredSessionData(): { userData: any; token: string } | null {
    const userDetails =
      localStorage.getItem('app_user_details') || sessionStorage.getItem('app_user_details');
    const token = localStorage.getItem('app_token') || sessionStorage.getItem('app_token');

    if (userDetails && token) {
      return { userData: JSON.parse(userDetails), token };
    }
    return null;
  }

  private restoreSession(sessionData: { userData: any; token: string }): void {
    sessionStorage.setItem('app_user_details', JSON.stringify(sessionData.userData));
    sessionStorage.setItem('app_token', sessionData.token);
    this.globalService.setCurrency(JSON.parse(sessionData.userData?.CurrencyJson).currencySymbols);
    this.globalService.setToken(sessionData.token);
    if (!this.isIframe) {
      this.globalService.triggerPendoIdentify();
    }
    this.loader.setLoadingState.set(true);

    setTimeout(() => {
      this.globalService.appInitialized.set(true);
    });

    if (this.globalService.isBusinessUnitSelected()) {
      this.loginService.fetchAndCacheLookupData().subscribe(
        () => {
          this.loader.setLoadingState.set(false);
        },
        () => {
          this.loader.setLoadingState.set(false);
        },
      );
    }
  }

  private performLogin(request?: any): Observable<boolean> {
    this.isInitializing = true;
    this.loader.setLoadingState.set(true);

    request = { scopes: environment.apiConfig.scopes };

    return from(
      firstValueFrom(this.msalService.acquireTokenSilent(request))
        .then((tokenResponse) => {
          if (tokenResponse) {
            return this.handleTokenResponse(tokenResponse);
          }
          this.completeInitialization(false);
          return false;
        })
        .catch(() => {
          this.completeInitialization(false);
          return false;
        }),
    );
  }

  private handleTokenResponse(tokenResponse: any): Promise<boolean> {
    if (!this.globalService.isBusinessUnitSelected()) {
      this.storeUserToken(tokenResponse.accessToken);
      this.msalService.instance.setActiveAccount(tokenResponse.account);
      this.setUserPref({
        Token: tokenResponse.accessToken,
      });
      this.router.navigate(['select-business-unit']);
      return firstValueFrom(of(true));
    }
    return firstValueFrom(
      this.loginService.getUserDetails().pipe(
        switchMap((user) => {
          this.storeUserToken(tokenResponse.accessToken);
          this.msalService.instance.setActiveAccount(tokenResponse.account);
          this.setUserPref({
            ...user.Result,
            Token: tokenResponse.accessToken,
          });
          this.storeUserDetails(user.Result);
          return this.loginService.fetchAndCacheLookupData();
        }),
        switchMap(() => of(true)),
        catchError((err) => {
          if (err?.Code === '409') {
            this.handleConflictError(err?.Message);
          }
          this.completeInitialization(false);
          return of(false);
        }),
      ),
    )
      .then((result) => {
        this.completeInitialization(result);
        return result;
      })
      .catch(() => {
        this.completeInitialization(false);
        return false;
      });
  }

  private storeUserToken(token: string): void {
    localStorage.setItem('app_token', token);
    sessionStorage.setItem('app_token', token);
  }

  storeUserDetails(userData: any): void {
    localStorage.setItem('selected-business-unit', userData.BusinessUnitId);
    localStorage.setItem('app_user_details', JSON.stringify(userData));
    sessionStorage.setItem('app_user_details', JSON.stringify(userData));
    this.globalService.setCurrency(JSON.parse(userData?.CurrencyJson).currencySymbols);
  }

  private completeInitialization(success: boolean): void {
    this.globalService.appInitialized.set(true);
    this.isInitializing = false;
    this.initializationSubject.next(success);
  }

  private setUserPref(res: any): void {
    this.globalService.setRefreshToken(null);
    this.globalService.setToken(res.Token);
    this.loginService.navigateToProjectList(false);
  }

  private handleConflictError(message: string): void {
    const dialog = this.modalService
      .openCustomWarningDialog({
        message,
        icon: 'i-alert-n',
        btnTxt2: 'cmn.ok',
        showIcon: true,
        showCheckBox: false,
        checkBoxMsg: '',
      })
      .subscribe(() => {
        dialog.unsubscribe();
        //  Only logout redirect in standalone window
        if (!this.isIframe && !this.isMsalAuthPopup) {
          localStorage.removeItem('selected-business-unit');
          this.msalService.logoutRedirect({});
        }
      });
  }
}
